/**
 * Client-side logging utility for the admin portal
 * Provides a simple interface for structured logging in the browser
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export class ClientLogger {
  private readonly component: string;
  private readonly minLevel: LogLevel;

  constructor(component: string, minLevel: LogLevel = LogLevel.INFO) {
    this.component = component;
    this.minLevel = minLevel;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };
    return levels[level] >= levels[this.minLevel];
  }

  private formatMessage(_level: LogLevel, message: string, context?: any): string {
    const timestamp = new Date().toISOString();
    const base = `[${timestamp}] [${this.component}] ${message}`;
    return context ? `${base} ${JSON.stringify(context)}` : base;
  }

  debug(message: string, context?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage(LogLevel.DEBUG, message, context));
    }
  }

  info(message: string, context?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage(LogLevel.INFO, message, context));
    }
  }

  warn(message: string, context?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(LogLevel.WARN, message, context));
    }
  }

  error(message: string, error?: Error | any, context?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      const fullMessage = error ? `${message}: ${errorMsg}` : message;
      console.error(this.formatMessage(LogLevel.ERROR, fullMessage, context));
    }
  }
}
